/**
 * Active Space/channel selection and unread clearing.
 *
 * Owns `setActiveSpace` (with same-slug skip-reload), `setActiveChannel`
 * (persists the last channel and clears its unread + decrements the Space
 * aggregate), and the unread helpers `clearChannelUnread` / `markSpaceRead`.
 * Opening a Space does **not** clear the primary-sidebar Space badge — that
 * only drops as each channel is viewed. Also fetches channel messages when a
 * Space resolves after a channel was already selected (direct deep-link nav).
 */

import {
  useCallback,
  useEffect,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import type { SpaceChannelUnreadState } from '../../services/spaceSocketHandlers';
import type { SpaceChannelMessagesState } from './types';

type FetchChannelMessages = (
  spaceId: string,
  channelId: string,
  cursor?: string,
  options?: { mergeLatest?: boolean; direction?: 'older' | 'newer' },
) => Promise<void> | void;

export interface SpaceActiveSelectionParams {
  activeSpaceIdRef: MutableRefObject<string | null>;
  activeSpaceSlugRef: MutableRefObject<string | null>;
  activeSpaceLoadingRef: MutableRefObject<boolean>;
  channelsLengthRef: MutableRefObject<number>;
  /** Active Space id (drives the deferred deep-link channel fetch). */
  activeSpaceId: string | null;
  activeChannelId: string | null;
  messagesByChannel: Record<string, SpaceChannelMessagesState>;
  resolveSpace: (slug: string) => Promise<void> | void;
  clearActiveSpace: () => void;
  fetchChannelMessages: FetchChannelMessages;
  setActiveChannelId: Dispatch<SetStateAction<string | null>>;
  setUnreadBySpace: Dispatch<SetStateAction<Record<string, number>>>;
  setUnreadByChannel: Dispatch<SetStateAction<Record<string, SpaceChannelUnreadState>>>;
  clearActiveSpacePermissions: () => void;
  /** Fired when a channel becomes active (e.g. to flip matching inbox rows). */
  onChannelActivated?: (channelId: string) => void;
}

export function useSpaceActiveSelection(params: SpaceActiveSelectionParams) {
  const {
    activeSpaceIdRef,
    activeSpaceSlugRef,
    activeSpaceLoadingRef,
    channelsLengthRef,
    activeSpaceId,
    activeChannelId,
    messagesByChannel,
    resolveSpace,
    clearActiveSpace,
    fetchChannelMessages,
    setActiveChannelId,
    setUnreadBySpace,
    setUnreadByChannel,
    clearActiveSpacePermissions,
    onChannelActivated,
  } = params;

  const onChannelActivatedRef = useRef(onChannelActivated);
  onChannelActivatedRef.current = onChannelActivated;

  /** Drop a channel's unread and recompute its Space aggregate from what remains. */
  const clearChannelUnreadAndSpaceAggregate = useCallback(
    (channelId: string) => {
      setUnreadByChannel((prev) => {
        const cleared = prev[channelId];
        if (!cleared) return prev;
        const spaceId = cleared.spaceId;
        const { [channelId]: _, ...rest } = prev;
        let total = 0;
        for (const state of Object.values(rest)) {
          if (state.spaceId === spaceId) total += state.unread;
        }
        setUnreadBySpace((spacePrev) => {
          if (total <= 0) {
            if (!spacePrev[spaceId]) return spacePrev;
            const { [spaceId]: _drop, ...spaceRest } = spacePrev;
            return spaceRest;
          }
          if (spacePrev[spaceId] === total) return spacePrev;
          return { ...spacePrev, [spaceId]: total };
        });
        return rest;
      });
    },
    [setUnreadByChannel, setUnreadBySpace],
  );

  const setActiveSpace = useCallback(
    (slug: string | null) => {
      if (slug === null) {
        // Reset now, not on the next render: a remount clears and re-selects
        // the same slug in one commit, and must not hit the skip below.
        activeSpaceSlugRef.current = null;
        channelsLengthRef.current = 0;
        clearActiveSpace();
        setActiveChannelId(null);
        clearActiveSpacePermissions();
        return;
      }
      // Skip full reload when the same Space is already loaded (refs avoid
      // changing this callback identity when channels finish loading). Keep the
      // Space unread badge — it only drops as channels are viewed.
      if (
        activeSpaceSlugRef.current === slug &&
        !activeSpaceLoadingRef.current &&
        channelsLengthRef.current > 0
      ) {
        return;
      }
      void resolveSpace(slug);
    },
    [
      resolveSpace,
      clearActiveSpace,
      clearActiveSpacePermissions,
      setActiveChannelId,
      activeSpaceSlugRef,
      activeSpaceLoadingRef,
      channelsLengthRef,
    ],
  );

  const setActiveChannel = useCallback(
    (channelId: string | null) => {
      setActiveChannelId(channelId);
      if (channelId) {
        clearChannelUnreadAndSpaceAggregate(channelId);
        onChannelActivatedRef.current?.(channelId);
      }
      const spaceId = activeSpaceIdRef.current;
      if (channelId && spaceId) {
        void fetchChannelMessages(spaceId, channelId);
        try {
          const key = `adieuu:lastChannel:${spaceId}`;
          localStorage.setItem(key, channelId);
        } catch {
          /* quota / SSR */
        }
      }
    },
    [
      fetchChannelMessages,
      setActiveChannelId,
      clearChannelUnreadAndSpaceAggregate,
      activeSpaceIdRef,
    ],
  );

  const clearChannelUnread = useCallback(
    (channelId: string) => {
      clearChannelUnreadAndSpaceAggregate(channelId);
    },
    [clearChannelUnreadAndSpaceAggregate],
  );

  const markSpaceRead = useCallback(
    (spaceId: string) => {
      setUnreadBySpace((prev) => {
        if (!prev[spaceId]) return prev;
        const { [spaceId]: _, ...rest } = prev;
        return rest;
      });
      setUnreadByChannel((prev) => {
        let changed = false;
        const next: Record<string, SpaceChannelUnreadState> = {};
        for (const [channelId, state] of Object.entries(prev)) {
          if (state.spaceId === spaceId) {
            changed = true;
            continue;
          }
          next[channelId] = state;
        }
        return changed ? next : prev;
      });
    },
    [setUnreadBySpace, setUnreadByChannel],
  );

  // When the space resolves after a channel was already selected (direct
  // navigation to /s/:slug/c/:channelId), fetch the channel's messages.
  const prevSpaceIdForChannelFetch = useRef<string | null>(null);
  useEffect(() => {
    const spaceId = activeSpaceId ?? null;
    if (
      spaceId &&
      spaceId !== prevSpaceIdForChannelFetch.current &&
      activeChannelId &&
      !messagesByChannel[activeChannelId]?.messages.length
    ) {
      void fetchChannelMessages(spaceId, activeChannelId);
    }
    prevSpaceIdForChannelFetch.current = spaceId;
  }, [activeSpaceId, activeChannelId, messagesByChannel, fetchChannelMessages]);

  return {
    setActiveSpace,
    setActiveChannel,
    clearChannelUnread,
    markSpaceRead,
  };
}
